import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { typography, radius } from '@/shared/theme/tokens';
import { HistoricalFilterBar, histDateParams } from '@/shared/ui/HistoricalFilterBar';
import { ViewToggle } from '@/shared/ui/ViewToggle';
import ReactMarkdown from 'react-markdown';
import { useParams, useSearchParams } from 'react-router-dom';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { timeAgo, formatTs } from '@/shared/lib/formatters';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { AppTable } from '@/shared/ui/AppTable';
import { FilterDrawer } from '@/shared/ui/FilterDrawer';
import { buildFilterParams } from '@/shared/lib/filterParams';
import {
  useRecommendationsLive,
  useRecommendationsHistorical,
  useRecommendationsFilterSchema,
} from '@/features/recommendations/hooks/useRecommendations';
import { RecommendationDrawer } from '@/features/recommendations/ui/RecommendationDrawer';
import {
  REC_STATUS_META as STATUS_META,
  REPEAT_BADGE,
  REC_ACCENT,
  REC_BRAND,
} from '@/features/recommendations/recommendationMeta';

function renderInlineMd(text) {
  if (!text) return null;
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : p);
}

const FILTERS = [
  { key: 'all',             label: 'All' },
  { key: 'unacknowledged',  label: 'Pending Review' },
  { key: 'accepted',        label: 'Acknowledged' },
  { key: 'rejected',        label: 'Rejected' },
  { key: 'acted_on',        label: 'Acted On' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────


function ActionDisplay({ action, unit, c }) {
  if (!action) return null;
  const isNumerical = action.type === 'numerical' || action.type === 'number';
  if (!isNumerical && action.instruction) {
    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 15, color: STATUS_META.accepted.color, flexShrink: 0, marginTop: 2 }}>assignment</span>
        <div style={{ fontSize: typography.fontSize.md, color: c.textLabel, lineHeight: 1.5 }}>
          <ReactMarkdown
            components={{
              p: ({ children }) => <p style={{ margin: '0 0 4px' }}>{children}</p>,
              strong: ({ children }) => <strong style={{ color: c.textPrimary }}>{children}</strong>,
              ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
              ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
              li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
            }}
          >{action.instruction}</ReactMarkdown>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ margin: '0 0 2px', fontSize: typography.fontSize.xs, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.semibold }}>Current</p>
        <p style={{ margin: 0, fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.bold, color: STATUS_META.rejected.color, lineHeight: 1 }}>
          {action.currentValue}
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.normal, marginLeft: 3 }}>{unit}</span>
        </p>
      </div>
      <span className="material-symbols-outlined" style={{ fontSize: 18, color: c.textSubtle }}>arrow_forward</span>
      <div style={{ textAlign: 'center' }}>
        <p style={{ margin: '0 0 2px', fontSize: typography.fontSize.xs, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.semibold }}>Recommended</p>
        <p style={{ margin: 0, fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.bold, color: STATUS_META.acted_on.color, lineHeight: 1 }}>
          {action.recommendedValue}
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.normal, marginLeft: 3 }}>{unit}</span>
        </p>
      </div>
    </div>
  );
}

function RecStatusBadge({ status }) {
  const m = STATUS_META[status] ?? STATUS_META.unacknowledged;
  return (
    <span style={{
      fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: m.color,
      background: m.bg, border: `1px solid ${m.border}`,
      borderRadius: radius.sm, padding: '2px 8px',
      textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label, whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
}

// ── View toggle ───────────────────────────────────────────────────────────────

// ── Filter bar ────────────────────────────────────────────────────────────────

function FilterBar({ active, onChange, counts, c, isDark }) {
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {FILTERS.map((f) => {
        const isActive = active === f.key;
        const count = f.key === 'all' ? undefined : counts[f.key] ?? 0;
        return (
          <button key={f.key} onClick={() => onChange(f.key)} style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            padding: '5px 12px', borderRadius: radius.full,
            border: `1px solid ${isActive ? REC_BRAND : c.border}`,
            background: isActive ? c.toggleActive : (isDark ? 'rgba(255,255,255,0.03)' : c.bg),
            color: isActive ? REC_BRAND : c.textSubtle,
            fontSize: typography.fontSize.base, fontWeight: isActive ? 600 : 400,
            fontFamily: typography.fontFamily.body,
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
            {f.label}
            {count !== undefined && (
              <span style={{
                fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
                background: isActive ? REC_BRAND : c.bgMuted,
                color: isActive ? '#fff' : c.textSubtle,
                borderRadius: radius.lg, padding: '1px 6px', minWidth: 18, textAlign: 'center',
              }}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Live card ─────────────────────────────────────────────────────────────────

function LiveCard({ rec, c, isDark, onOpen }) {
  const [hov, setHov] = useState(false);

  return (
    <div
      onClick={() => onOpen?.(rec)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: c.bg,
        border: `1px solid ${hov ? 'rgba(62,181,231,0.3)' : c.border}`,
        borderRadius: radius['2xl'], padding: '18px 20px',
        display: 'flex', flexDirection: 'column', gap: '14px',
        boxShadow: hov
          ? (isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 6px 20px rgba(30,67,150,0.10)')
          : (isDark ? 'none' : '0 2px 6px rgba(30,67,150,0.04)'),
        transition: 'all 0.18s',
        transform: hov ? 'translateY(-1px)' : 'none',
        cursor: 'pointer',
      }}
    >
      {/* Row 1: parameter + badges */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: REC_ACCENT, flexShrink: 0, marginTop: 1, fontVariationSettings: "'FILL' 1" }}>
          lightbulb
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: '0 0 3px', fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>{rec.parameterName}</p>
          <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: c.textSubtle, fontFamily: typography.fontFamily.mono }}>{rec.dataKey}</p>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <RecStatusBadge status={rec.status} />
        </div>
      </div>

      {/* Row 2: action */}
      <div style={{
        background: isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle,
        borderRadius: radius.lg, padding: '12px 16px',
      }}>
        <p style={{ margin: '0 0 8px', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>Action</p>
        <ActionDisplay action={rec.action} unit={rec.unit} c={c} />
      </div>

      {/* Row 3: expected impact + time to impact */}
      {rec.expectedImpact || rec.timeToImpact ? (
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '12px', alignItems: 'start' }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>Expected Impact</p>
            <div style={{ fontSize: typography.fontSize.base, color: c.textLabel, lineHeight: 1.5 }}>
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p style={{ margin: '0 0 4px' }}>{children}</p>,
                  strong: ({ children }) => <strong style={{ color: c.textPrimary }}>{children}</strong>,
                  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 16 }}>{children}</ul>,
                  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 16 }}>{children}</ol>,
                  li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
                }}
              >{rec.expectedImpact ?? '—'}</ReactMarkdown>
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <p style={{ margin: '0 0 4px', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>Time to Impact</p>
            <p style={{ margin: 0, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textLabel }}>{rec.timeToImpact ?? '—'}</p>
          </div>
        </div>
      ) : rec.impacts?.length ? (
        <div>
          <p style={{ margin: '0 0 6px', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>Expected Impact</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {rec.impacts.map((imp, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: typography.fontSize.base, color: c.textLabel, lineHeight: 1.4 }}>
                  <span style={{ fontWeight: typography.fontWeight.semibold }}>{imp.expected}</span>
                  {imp.variable ? <span style={{ color: c.textSubtle }}> {imp.variable}</span> : null}
                </span>
                {imp.delay && <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, flexShrink: 0 }}>{imp.delay}</span>}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Row 4: predecessor tag + timestamp + actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textSubtle }}>{formatTs(rec.generatedAt)}</span>
          {rec.previousSameCount > 0 && (
            <span style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: REPEAT_BADGE.color,
              background: REPEAT_BADGE.bg, border: `1px solid ${REPEAT_BADGE.border}`,
              borderRadius: radius.sm, padding: '2px 7px', display: 'flex', alignItems: 'center', gap: '3px',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 11 }}>repeat</span>
              Same as last {rec.previousSameCount}
            </span>
          )}
        </div>

        <button onClick={(e) => { e.stopPropagation(); onOpen?.(rec); }} style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textActive,
          fontFamily: typography.fontFamily.body,
        }}>
          View Details
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        </button>
      </div>
    </div>
  );
}

// ── Historical row ─────────────────────────────────────────────────────────────

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ message, c }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '12px' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color: c.textSubtle, opacity: 0.35 }}>lightbulb</span>
      <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textSubtle }}>{message}</p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DeploymentRecommendationsPage() {
  const { id: deploymentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const c = useThemeColors();
  const isDark = useIsDark();

  const view = searchParams.get('view') === 'historical' ? 'historical' : 'live';
  const recIdParam = searchParams.get('rec');

  const [filter, setFilter] = useState('all');
  const [histDateRange, setHistDateRange] = useState('7d');
  const [selectedRec, setSelectedRec] = useState(null);
  const [isHistoricalRec, setIsHistoricalRec] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeGenRef = useRef(0);

  function setView(v) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('view', v);
      p.delete('rec');
      return p;
    }, { replace: true });
    setFilter('all');
    setFiltersOpen(false);
  }

  const openDrawer = useCallback((rec, historical = false) => {
    setSelectedRec(rec);
    setIsHistoricalRec(historical);
    setDrawerOpen(true);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('rec', rec.id);
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  function closeDrawer() {
    setDrawerOpen(false);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('rec');
      return p;
    }, { replace: true });
    const gen = ++closeGenRef.current;
    setTimeout(() => { if (closeGenRef.current === gen) setSelectedRec(null); }, 300);
  }

  // DataGrid model updates instantly; the query keys off a DEBOUNCED model so
  // rapid page clicks collapse into a single request for the final page.
  const [histModel, setHistModel] = useState({ page: 0, pageSize: 10 });
  const { data: filterSchema } = useRecommendationsFilterSchema(deploymentId);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [committedFilters, setCommittedFilters] = useState({});
  const [committedSort, setCommittedSort] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filterParams = useMemo(
    () => buildFilterParams(committedFilters, debouncedSearch, committedSort),
    [committedFilters, debouncedSearch, committedSort],
  );

  const debouncedModel = useDebouncedValue(histModel, 350);
  const histParams = useMemo(
    () => ({ ...histDateParams(histDateRange), ...filterParams, page: debouncedModel.page + 1, pageSize: debouncedModel.pageSize }),
    [histDateRange, filterParams, debouncedModel.page, debouncedModel.pageSize],
  );

  const { data: liveRecs = [], isLoading: liveLoading, isError: liveError, error: liveErr } = useRecommendationsLive(deploymentId);
  const { data: histData, isLoading: histLoading, isFetching: histFetching, isError: histError, error: histErr } = useRecommendationsHistorical(deploymentId, histParams);
  const histRecs = useMemo(() => histData?.rows ?? [], [histData]);
  const histPagination = histData?.pagination ?? null;
  // Busy while mid-burst (model ahead of debounced query) or a fetch is running.
  const histBusy = histFetching || search !== debouncedSearch || histModel.page !== debouncedModel.page || histModel.pageSize !== debouncedModel.pageSize;

  // Reset to first page when the date range changes (current page may be out of range).
  function changeHistRange(range) {
    setHistDateRange(range);
    setHistModel((m) => ({ ...m, page: 0 }));
  }

  const activeFilterCount = useMemo(() => {
    let n = Object.values(committedFilters).filter((e) => {
      if (!e) return false;
      if (e.type === 'categorical') return (e.value ?? []).length > 0;
      return (e.min ?? '') !== '' || (e.max ?? '') !== '';
    }).length;
    if (committedSort?.by) n += 1;
    return n;
  }, [committedFilters, committedSort]);

  function applyFilters(filters, sort) {
    setCommittedFilters(filters);
    setCommittedSort(sort);
    setFiltersOpen(false);
    setHistModel((m) => ({ ...m, page: 0 }));
  }
  function clearFilters() {
    setCommittedFilters({});
    setCommittedSort(null);
    setHistModel((m) => ({ ...m, page: 0 }));
  }
  function changeSearch(text) {
    setSearch(text);
    setHistModel((m) => ({ ...m, page: 0 }));
  }

  // Restore drawer from URL param once data loads
  useEffect(() => {
    if (!recIdParam || drawerOpen) return;
    const inLive = liveRecs.find((r) => r.id === recIdParam);
    if (inLive) { openDrawer(inLive, false); return; }
    const inHist = histRecs.find((r) => r.id === recIdParam);
    if (inHist) { openDrawer(inHist, true); }
  }, [recIdParam, drawerOpen, liveRecs, histRecs, openDrawer]);

  const isLoading = view === 'live' ? liveLoading : histLoading;
  const isError = view === 'live' ? liveError : histError;
  const loadError = view === 'live' ? liveErr : histErr;

  const liveCounts = Object.fromEntries(
    ['unacknowledged', 'accepted', 'rejected', 'acted_on'].map((s) => [s, liveRecs.filter((r) => r.status === s).length])
  );

  const filteredLive = filter === 'all' ? liveRecs : liveRecs.filter((r) => r.status === filter);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: typography.fontFamily.body }}>
      <PageHeader
        title="Recommendations"
        description="AI-powered process optimisation suggestions"
        actions={<ViewToggle view={view} onChange={setView} c={c} isDark={isDark} />}
      />

      {/* Content */}
      {isError ? (
        <ErrorState message={loadError?.message ?? 'Failed to load recommendations.'} />
      ) : view === 'live' ? (
        isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[1, 2, 3].map((i) => <SkeletonCard key={i} rows={4} height={160} />)}
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <FilterBar active={filter} onChange={setFilter} counts={liveCounts} c={c} isDark={isDark} />
            <LiveView recs={filteredLive} deploymentId={deploymentId} c={c} isDark={isDark} onOpen={(r) => openDrawer(r, false)} />
          </div>
        )
      ) : (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <HistoricalFilterBar
            dateRange={histDateRange}
            onDateRange={changeHistRange}
            c={c}
            isDark={isDark}
            search={search}
            onSearch={changeSearch}
            onOpenFilters={filterSchema ? () => setFiltersOpen(true) : undefined}
            activeFilterCount={activeFilterCount}
          />
          <HistoricalView
            recs={histRecs}
            isLoading={histLoading || histBusy}
            c={c}
            isDark={isDark}
            onOpen={(r) => openDrawer(r, true)}
            serverPagination={histPagination ? {
              rowCount: histPagination.total,
              paginationModel: histModel,
              onPaginationModelChange: setHistModel,
            } : undefined}
          />
          {filterSchema && (
            <FilterDrawer
              open={filtersOpen}
              onClose={() => setFiltersOpen(false)}
              schema={filterSchema}
              initialFilters={committedFilters}
              initialSort={committedSort}
              onApply={applyFilters}
              onClear={clearFilters}
            />
          )}
        </div>
      )}

      {selectedRec && (() => {
        const activeList = isHistoricalRec ? histRecs : filteredLive;
        const recIndex = activeList.findIndex((r) => r.id === selectedRec.id);
        return (
          <RecommendationDrawer
            rec={selectedRec}
            depId={deploymentId}
            isHistorical={isHistoricalRec}
            onClose={closeDrawer}
            isOpen={drawerOpen}
            recs={activeList}
            recIndex={recIndex === -1 ? 0 : recIndex}
            onNavigate={(idx) => { if (activeList[idx]) openDrawer(activeList[idx], isHistoricalRec); }}
          />
        );
      })()}
    </div>
  );
}

// ── Live view ─────────────────────────────────────────────────────────────────

function LiveView({ recs, deploymentId, c, isDark, onOpen }) {
  if (!recs.length) return <EmptyState message="No recommendations match this filter." c={c} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {recs.map((r) => <LiveCard key={r.id} rec={r} deploymentId={deploymentId} c={c} isDark={isDark} onOpen={onOpen} />)}
    </div>
  );
}

// ── Historical view ───────────────────────────────────────────────────────────

function useHistoricalColumns(c, onOpen) {
  return [
    {
      field: 'parameterName',
      headerName: 'Parameter & Action',
      flex: 2,
      minWidth: 200,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', padding: '10px 0' }}>
          <span style={{ fontWeight: typography.fontWeight.bold, fontSize: typography.fontSize.md, color: c.textPrimary, lineHeight: 1.3 }}>
            {row.parameterName}
          </span>
          <span style={{ fontSize: typography.fontSize.sm, color: c.textMuted, lineHeight: 1.4 }}>
            {row.action?.type === 'numerical'
              ? `${row.action.currentValue} → ${row.action.recommendedValue} ${row.unit}`
              : renderInlineMd(row.action?.instruction)}
          </span>
        </div>
      ),
    },
    {
      field: 'generatedAt',
      headerName: 'Raised At',
      flex: 1.1,
      minWidth: 110,
      renderCell: ({ value }) => value ? (
        <div>
          <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{timeAgo(value)}</div>
          <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: 2 }}>{formatTs(value)}</div>
        </div>
      ) : <span style={{ color: c.textSubtle }}>—</span>,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 110,
      renderCell: ({ value }) => {
        const sm = STATUS_META[value] ?? STATUS_META.expired;
        return (
          <span style={{
            fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: sm.color,
            background: sm.bg, border: `1px solid ${sm.border}`,
            borderRadius: radius.sm, padding: '2px 8px',
            textTransform: 'uppercase', letterSpacing: typography.letterSpacing.label, whiteSpace: 'nowrap',
          }}>
            {sm.label}
          </span>
        );
      },
    },
    {
      field: 'takenBy',
      headerName: 'Taken By',
      flex: 1,
      minWidth: 120,
      renderCell: ({ value }) => (
        <span style={{ fontSize: typography.fontSize.md, color: value ? c.textPrimary : c.textSubtle }}>{value ?? '—'}</span>
      ),
    },
    {
      field: 'impacts',
      headerName: 'Expected Impact',
      flex: 2,
      minWidth: 200,
      sortable: false,
      renderCell: ({ value }) => {
        const impacts = value;
        if (!impacts?.length) return <span style={{ fontSize: typography.fontSize.md, color: c.textSubtle }}>—</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0', width: '100%' }}>
            {impacts.map((imp, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, flexShrink: 0 }}>{imp.variable}</span>
                <span style={{
                  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
                  color: imp.actual ? STATUS_META.acted_on.color : c.textSubtle,
                  whiteSpace: 'nowrap',
                }}>
                  {imp.actual ?? imp.expected ?? '—'}
                </span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: '__actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); onOpen?.(row); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '3px',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: c.textActive,
            fontFamily: typography.fontFamily.body,
          }}
        >
          View Details
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        </button>
      ),
    },
  ];
}

function HistoricalView({ recs, isLoading, c, isDark: _isDark, onOpen, serverPagination }) {
  const columns = useHistoricalColumns(c, onOpen);
  if (!isLoading && !recs.length) return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <EmptyState message="No historical recommendations yet." c={c} />
    </div>
  );
  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    <AppTable
      rows={recs}
      columns={columns}
      getRowId={(row) => row.id}
      getRowHeight={() => 'auto'}
      getEstimatedRowHeight={() => 64}
      onRowClick={({ row }) => onOpen?.(row)}
      loading={isLoading}
      serverPagination={serverPagination}
      disableColumnMenu
      disableColumnSorting
      extraSx={{
        '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: '8px' },
        '& .MuiDataGrid-row': { cursor: 'pointer' },
      }}
    />
    </div>
  );
}
